"use client";

import dynamic from "next/dynamic";
import Script from "next/script";
import { usePathname } from "next/navigation";
import {
  createContext,
  useContext,
  useEffect,
  useRef,
  useState,
  type ReactNode,
} from "react";
import { createChatManager } from "@/lib/chat/chat";
import {
  DISABLED_CHAT_CAPABILITIES,
  disabledChatResult,
  type ChatClient,
  type ChatScriptDescriptor,
  type PublicChatConfig,
} from "@/lib/chat/provider";
import {
  getConsentState,
  hydrateConsentState,
  readLocalStorage,
  subscribeToConsentState,
  type ConsentState,
} from "@/lib/consent";

const disabledClient: ChatClient = {
  provider: null,
  capabilities: DISABLED_CHAT_CAPABILITIES,
  initialize: async () => disabledChatResult("initialize"),
  identifyUser: async () => disabledChatResult("identify_user"),
  logout: async () => disabledChatResult("logout"),
  show: async () => disabledChatResult("show"),
  hide: async () => disabledChatResult("hide"),
  open: async () => disabledChatResult("open"),
  close: async () => disabledChatResult("close"),
  trackEvent: async () => disabledChatResult("track_event"),
};

const ChatContext = createContext<ChatClient>(disabledClient);

/**
 * Crisp-only, and vendor-specific by design — it drives `window.$crisp`
 * directly rather than the shared `ChatClient`. Loaded on demand so its code
 * never reaches visitors on another provider.
 */
const CrispNudges = dynamic(() => import("./CrispNudges"), { ssr: false });

interface ChatProviderProps {
  config: PublicChatConfig;
  children: ReactNode;
}

export function ChatProvider({ config, children }: ChatProviderProps) {
  const pathname = usePathname() ?? "/";
  const managerReference = useRef<ReturnType<typeof createChatManager> | null>(null);
  const hasLoggedConfigurationError = useRef(false);
  const [script, setScript] = useState<ChatScriptDescriptor | null>(null);
  const [consentState, setConsentState] = useState<ConsentState>(getConsentState());

  if (!managerReference.current) {
    managerReference.current = createChatManager(config);
  }
  const manager = managerReference.current;
  const isAdminRoute = pathname.startsWith("/admin");
  const theme = isAdminRoute ? "light" : "dark";
  const chatConsentGranted = consentState === "accepted";
  // Visitor engagement is a public-marketing-site concern; admins in the CMS
  // should not be nudged. Also withheld until the visitor has actually
  // consented, since nudging would otherwise write to sessionStorage and
  // reach for a widget that isn't allowed to load yet.
  const nudgesEnabled =
    config.enabled && config.provider === "crisp" && !isAdminRoute && chatConsentGranted;

  useEffect(() => manager.subscribeToScript(setScript), [manager]);

  useEffect(() => {
    hydrateConsentState(readLocalStorage());
    return subscribeToConsentState(setConsentState);
  }, []);

  // `<body data-chat-provider>` (set server-side in app/layout.tsx from the
  // static config alone) drives `.chat-safe-floating-cta`'s mobile bottom
  // offset in globals.css, reserving room above the chat launcher bubble.
  // That's only warranted once the widget can actually load — a visitor who
  // has rejected consent will never see the bubble, so the reserved space
  // is just a dead gap under the sticky CTA bar. Keep the attribute (and the
  // gap) for "pending"/"unset" too: the banner is still up and the widget
  // may yet load, so shrinking now would mean jumping back to the wider gap
  // the moment they accept.
  useEffect(() => {
    if (typeof document === "undefined") return;
    if (config.enabled && consentState !== "rejected") {
      document.body.setAttribute("data-chat-provider", config.provider);
    } else {
      document.body.removeAttribute("data-chat-provider");
    }
  }, [config, consentState]);

  useEffect(() => {
    if (!config.enabled) {
      if (config.reason && !hasLoggedConfigurationError.current) {
        hasLoggedConfigurationError.current = true;
        console.error(config.reason);
      }
      return;
    }

    if (!chatConsentGranted) return;

    void manager.initialize().catch(() => undefined);
  }, [config, manager, chatConsentGranted]);

  useEffect(() => {
    if (!chatConsentGranted) return;

    void manager.setTheme(theme).catch(() => undefined);
    void manager.updateRoute(pathname).catch(() => undefined);
  }, [manager, pathname, theme, chatConsentGranted]);

  return (
    <ChatContext.Provider value={manager}>
      {children}
      {config.enabled && config.provider === "crisp" && chatConsentGranted && (
        <link rel="preconnect" href="https://client.crisp.chat" />
      )}
      {nudgesEnabled && <CrispNudges />}
      {script && (
        <Script
          id={script.id}
          src={script.src}
          strategy="afterInteractive"
          data-chat-provider={manager.provider ?? undefined}
          onLoad={() => {
            void managerReference.current
              ?.handleScriptReady()
              .catch(() => undefined);
          }}
          onReady={() => {
            void managerReference.current
              ?.handleScriptReady()
              .catch(() => undefined);
          }}
          onError={() => {
            void managerReference.current
              ?.handleScriptError()
              .catch(() => undefined);
          }}
        />
      )}
    </ChatContext.Provider>
  );
}

export const useChat = () => useContext(ChatContext);
